import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Radio,
  Select,
  DatePicker,
  message,
  Col,
  Row,
  Upload,
  Space,
} from "antd";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createUserSchema,
  updateUserSchema,
} from "../../validations/userSchema";
import {
  useCreateUser,
  useUpdateUser,
  useUserTypes,
} from "../../hooks/useUsers";
import { useLocations } from "../../hooks/useLocation";
import { useRoles } from "../../hooks/useRoles";
import { uploadImageToCloudinary } from "../../helpers/cloudinary";
import { UploadOutlined, LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { fetchStates, fetchLGAs } from "../../helpers/locationApi";

interface AddNewUserModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  user?: {
    id: number;
    name: string;
    email: string;
    password?: string;
    user_type_id: number;
    role_id?: number;
    location_id: number;
    date_of_birth?: string;
    gender: string;
    country: string;
    state: string;
    lga: string;
    address?: string;
    phone: string;
    whatsapp_phone?: string;
    avatar?: string;
    status: boolean;
    roles?: {
      id: number;
      name: string;
      description?: string;
    };
    user_type?: {
      id: number;
      name: string;
      description?: string;
      icon?: string;
    };
    location?: {
      id: number;
      name: string;
      address?: string;
      code?: string;
    };
    created_at?: string;
    updated_at?: string;
    deleted_at?: string | null;
    email_verified_at?: string;
  } | null;
}

// Type for the form values
type FormValues = {
  name: string;
  email: string;
  password: string;
  user_type_id: number;
  role_id: number;
  location_id: number;
  date_of_birth?: string;
  gender: "male" | "female";
  country: string;
  state: string;
  lga: string;
  address?: string;
  phone: string;
  whatsapp_phone?: string;
  avatar?: string;
  status: boolean;
};

// Helper component for required field label
const RequiredFieldLabel: React.FC<{ label: string }> = ({ label }) => (
  <span>
    {label} <span className="text-red-500">*</span>
  </span>
);

const AddNewUserModal: React.FC<AddNewUserModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  user,
}) => {
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [states, setStates] = useState<string[]>([]);
  const [lgas, setLgas] = useState<string[]>([]);

  // Choose the appropriate schema based on whether we're editing or creating
  const isEditMode = !!user;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(isEditMode ? updateUserSchema : createUserSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      user_type_id: 0,
      role_id: 0,
      location_id: 0,
      gender: "male",
      country: "Nigeria",
      state: "",
      lga: "",
      phone: "",
      status: true,
    },
  });

  // Use both create and update mutations
  const { mutate: createUser, isPending: isCreating } = useCreateUser();
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUser();
  const { data: locations } = useLocations();
  const { data: userTypes } = useUserTypes();
  const { data: roles } = useRoles();

  const selectedState = watch("state");
  const isPending = isCreating || isUpdating;

  // Handle form closing and resetting
  const handleCancel = () => {
    reset({
      name: "",
      email: "",
      password: "",
      user_type_id: 0,
      role_id: 0,
      location_id: 0,
      gender: "male",
      country: "Nigeria",
      state: "",
      lga: "",
      phone: "",
      status: true,
    });
    setAvatarUrl(null);
    onCancel();
  };

  // Handle avatar upload
  const handleAvatarUpload = async (file: File) => {
    try {
      setUploadingAvatar(true);
      const uploadedUrl = await uploadImageToCloudinary(file);
      setAvatarUrl(uploadedUrl);
      setValue("avatar", uploadedUrl);
      setUploadingAvatar(false);
      message.success("Avatar uploaded successfully!");
      return uploadedUrl;
    } catch (error) {
      setUploadingAvatar(false);
      message.error("Failed to upload avatar. Please try again.");
      console.error("Avatar upload error:", error);
      return null;
    }
  };

  // Fetch states when modal opens
  useEffect(() => {
    if (visible) {
      fetchStates()
        .then((data) => setStates(data))
        .catch((error) => {
          console.error(error);
          message.error(error.message);
        });
    }
  }, [visible]);

  // Fetch LGAs when state changes
  useEffect(() => {
    if (selectedState) {
      fetchLGAs(selectedState)
        .then((data) => setLgas(data))
        .catch((error) => {
          console.error(error);
          message.error(error.message);
        });
    } else {
      setLgas([]);
    }
  }, [selectedState]);

  // Reset form when visibility changes
  useEffect(() => {
    if (!visible) {
      reset({
        name: "",
        email: "",
        password: "",
        user_type_id: 0,
        role_id: 0,
        location_id: 0,
        gender: "male",
        country: "Nigeria",
        state: "",
        lga: "",
        phone: "",
        status: true,
      });
      setAvatarUrl(null);
    }
  }, [visible, reset]);

  // Populate form fields when user data is provided
  // Modified useEffect for populating form fields when user data is provided
  useEffect(() => {
    if (user && visible) {
      const defaultValues: Partial<FormValues> = {
        name: user.name,
        email: user.email,
        password: "", // Don't populate password for existing users
        user_type_id: user.user_type_id,
        role_id: user.role_id || user.roles?.id || 0,
        location_id: user.location_id,
        date_of_birth: user.date_of_birth
          ? dayjs(user.date_of_birth).format("YYYY-MM-DD")
          : undefined,
        gender:
          user.gender === "male" || user.gender === "female"
            ? (user.gender.toLowerCase() as "male" | "female")
            : "male",
        country: user.country,
        state: user.state,
        lga: user.lga,
        address: user.address || "",
        phone: user.phone,
        // Ensure these fields always have values even if they're empty in the user data
        whatsapp_phone: user.whatsapp_phone || "",
        avatar: user.avatar || "",
        status:
          typeof user.status === "number" ? user.status === 1 : !!user.status,
      };

      // Reset with complete values
      reset(defaultValues);

      // Set the avatar URL if it exists
      setAvatarUrl(user.avatar || null);

      // Fetch LGAs if the user's state is already set
      if (user.state) {
        fetchLGAs(user.state)
          .then((data) => setLgas(data))
          .catch((error) => {
            console.error(error);
            message.error(error.message);
          });
      }
    }
  }, [user, reset, visible]);

  // Modified onSubmitForm for better handling of optional fields
  const onSubmitForm = async (data: FormValues) => {
    // Ensure optional fields have at least empty strings
    const processedData = {
      ...data,
      address: data.address || "",
      whatsapp_phone: data.whatsapp_phone || "",
      date_of_birth: data.date_of_birth || "",
      avatar: data.avatar || "",
    };

    const basePayload = {
      ...processedData,
      date_of_birth: processedData.date_of_birth
        ? dayjs(processedData.date_of_birth).format("YYYY-MM-DD")
        : "",
      avatar: avatarUrl || processedData.avatar || "",
    };

    if (user && user.id) {
      // If user exists, perform update
      // For updates, create a new payload without password if it's empty
      const updatePayload = { ...basePayload };

      // Instead of deleting, create a new object without the password property
      if (!updatePayload.password) {
        const { password, ...payloadWithoutPassword } = updatePayload;
        updateUser(
          { userId: user.id, userData: payloadWithoutPassword },
          {
            onSuccess: () => {
              message.success("User updated successfully!");
              reset();
              setAvatarUrl(null);
              onSubmit();
              onCancel();
            },
            onError: (error: any) => {
              console.error("User update error:", error);
              message.error("Failed to update user. Please try again.");
            },
          }
        );
      } else {
        // If password is provided, use the full payload
        updateUser(
          { userId: user.id, userData: updatePayload },
          {
            onSuccess: () => {
              message.success("User updated successfully!");
              reset();
              setAvatarUrl(null);
              onSubmit();
              onCancel();
            },
            onError: (error: any) => {
              console.error("User update error:", error);
              message.error("Failed to update user. Please try again.");
            },
          }
        );
      }
    } else {
      // If no user, perform create
      createUser(basePayload, {
        onSuccess: () => {
          message.success("User created successfully!");
          reset();
          setAvatarUrl(null);
          onSubmit();
          onCancel();
        },
        onError: (error: any) => {
          console.error("User creation error:", error);
          message.error("Failed to create user. Please try again.");
        },
      });
    }
  };
  // Custom upload button for avatar
  const uploadButton = (
    <div>
      {uploadingAvatar ? <LoadingOutlined /> : <UploadOutlined />}
      <div style={{ marginTop: 8 }}>Upload Avatar</div>
    </div>
  );

  return (
    <Modal
      title={user ? "Edit User" : "Add New User"}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose={true}
    >
      <Form layout="vertical" onFinish={handleSubmit(onSubmitForm)}>
        {/* Avatar Upload */}
        {/* Avatar Upload */}
        <Row justify="center" style={{ marginBottom: 16 }}>
          <Col>
            <Form.Item label="Avatar">
              <Upload
                name="avatar"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleAvatarUpload(file);
                  return false; // Prevent default upload behavior
                }}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
              {/* Make sure avatar has a default value and doesn't show validation errors */}
              <Controller
                name="avatar"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <input
                    type="hidden"
                    {...field}
                    value={field.value || avatarUrl || ""} // Ensure it always has a value
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Full Name and Email */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="Full Name" />}
                  validateStatus={errors.name ? "error" : ""}
                  help={errors.name?.message}
                >
                  <Input {...field} placeholder="Enter full name" />
                </Form.Item>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="Email" />}
                  validateStatus={errors.email ? "error" : ""}
                  help={errors.email?.message}
                >
                  <Input {...field} placeholder="Enter email" />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        {/* Password and User Type */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={
                    user ? (
                      "New Password (leave blank to keep current)"
                    ) : (
                      <RequiredFieldLabel label="Password" />
                    )
                  }
                  validateStatus={errors.password ? "error" : ""}
                  help={errors.password?.message}
                >
                  <Input.Password
                    {...field}
                    placeholder={user ? "Enter new password" : "Enter password"}
                  />
                </Form.Item>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="user_type_id"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="User Type" />}
                  validateStatus={errors.user_type_id ? "error" : ""}
                  help={errors.user_type_id?.message}
                >
                  <Select {...field} placeholder="Select user type">
                    {userTypes &&
                      Object.entries(userTypes)?.map(([id, name]) => (
                        <Select.Option key={id} value={Number(id)}>
                          {name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        {/* User Role and Location */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Controller
              name="role_id"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="User Role" />}
                  validateStatus={errors.role_id ? "error" : ""}
                  help={errors.role_id?.message}
                >
                  <Select {...field} placeholder="Select user role">
                    {roles &&
                      roles.map((role) => (
                        <Select.Option key={role.id} value={role.id}>
                          {role.name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="location_id"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="Location" />}
                  validateStatus={errors.location_id ? "error" : ""}
                  help={errors.location_id?.message}
                >
                  <Select {...field} placeholder="Select location">
                    {locations &&
                      locations?.map((location) => (
                        <Select.Option key={location.id} value={location.id}>
                          {location.name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        {/* Date of Birth and Gender */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Controller
              name="date_of_birth"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label="Date of Birth"
                  validateStatus={errors.date_of_birth ? "error" : ""}
                  help={errors.date_of_birth?.message}
                >
                  <DatePicker
                    {...field}
                    format="YYYY-MM-DD"
                    style={{ width: "100%" }}
                    placeholder="Select date of birth"
                    value={field.value ? dayjs(field.value as string) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.format("YYYY-MM-DD") : null)
                    }
                  />
                </Form.Item>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="Gender" />}
                  validateStatus={errors.gender ? "error" : ""}
                  help={errors.gender?.message}
                >
                  <Radio.Group {...field}>
                    <Radio value="male">Male</Radio>
                    <Radio value="female">Female</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        {/* Country and State */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="Country" />}
                  validateStatus={errors.country ? "error" : ""}
                  help={errors.country?.message}
                >
                  <Input {...field} disabled />
                </Form.Item>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="State" />}
                  validateStatus={errors.state ? "error" : ""}
                  help={errors.state?.message}
                >
                  <Select {...field} placeholder="Select state">
                    {states?.map((state) => (
                      <Select.Option key={state} value={state}>
                        {state}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        {/* LGA and Address */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Controller
              name="lga"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label="LGA"
                  validateStatus={errors.lga ? "error" : ""}
                  help={errors.lga?.message}
                >
                  <Select
                    {...field}
                    placeholder="Select LGA"
                    disabled={!selectedState}
                  >
                    {lgas?.map((lga) => (
                      <Select.Option key={lga} value={lga}>
                        {lga}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label="Address"
                  validateStatus={errors.address ? "error" : ""}
                  help={errors.address?.message}
                >
                  <Input {...field} placeholder="Enter address" />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        {/* Phone and WhatsApp Phone */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="Phone" />}
                  validateStatus={errors.phone ? "error" : ""}
                  help={errors.phone?.message}
                >
                  <Input {...field} placeholder="Enter phone number" />
                </Form.Item>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="whatsapp_phone"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label="WhatsApp Phone"
                  validateStatus={errors.whatsapp_phone ? "error" : ""}
                  help={errors.whatsapp_phone?.message}
                >
                  <Input {...field} placeholder="Enter WhatsApp phone number" />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        {/* Status */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={<RequiredFieldLabel label="Status" />}
                  validateStatus={errors.status ? "error" : ""}
                  help={errors.status?.message}
                >
                  <Radio.Group {...field}>
                    <Radio value={true}>Active</Radio>
                    <Radio value={false}>Inactive</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        {/* Form Actions */}
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={isPending}>
              {user ? "Update" : "Submit"}
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddNewUserModal;
